/**
 * Which palette the site paints in, and how that decision survives a reload.
 *
 * Three CHOICES, two OUTCOMES. `system` is a choice that resolves to one of the
 * other two at read time; the document itself is only ever stamped `light` or
 * `dark`, so the CSS has one rule to follow instead of a media query racing an
 * attribute.
 *
 * The default is DARK, not `system`. The site was designed dark and a visitor
 * who has expressed no preference here should see the design as intended —
 * `system` is available, it is simply not assumed.
 */

export const THEME_CHOICES = ['system', 'light', 'dark'] as const
export type ThemeChoice = (typeof THEME_CHOICES)[number]

/** What actually gets stamped on `<html>`. */
export type ResolvedTheme = 'light' | 'dark'

export const THEME_STORAGE_KEY = 'flou:theme'

/** No choice stored, or something unreadable in storage. */
export const DEFAULT_THEME_CHOICE: ThemeChoice = 'dark'

export const THEME_LABELS: Record<ThemeChoice, string> = {
  system: 'Jak w systemie',
  light: 'Jasny',
  dark: 'Ciemny',
}

export function isThemeChoice(value: unknown): value is ThemeChoice {
  return typeof value === 'string' && (THEME_CHOICES as readonly string[]).includes(value)
}

/**
 * The script that runs BEFORE first paint.
 *
 * It has to be inline and synchronous in `<head>`: anything deferred paints the
 * default palette first, and a light-mode visitor watches the page flash black
 * on every navigation. That is also why it is a string rather than a module —
 * it must execute before React exists.
 *
 * Wrapped in try/catch because `localStorage` throws outright in a few real
 * situations (Safari private browsing, storage disabled by policy, a sandboxed
 * frame). A theme preference is not worth a blank page, so a failure here falls
 * through to the markup's own default.
 *
 * Written against `document.documentElement` directly rather than through any
 * helper below: this string is evaluated in a context where nothing else from
 * this module exists.
 */
/**
 * Baskets that used to live in this browser, swept once and then forgotten.
 *
 * The basket moved to Postgres behind a `HttpOnly` cookie, so these keys are
 * dead — but a stale one is a shopping list of somebody's intentions sitting in
 * a store any script on the page can read, for as long as that browser lives.
 * Nothing else runs early enough on every page to remove them, which is the only
 * reason this rides along with the theme boot script.
 *
 * **Delete this after 2027-03-01.** By then anybody still carrying a v4 basket
 * has not opened the site in six months, and the lines are three versions of the
 * catalogue out of date.
 */
const ORPHANED_CART_KEYS = ['flou:cart:v2', 'flou:cart:v3', 'flou:cart:v4']

export const THEME_SCRIPT = `(function(){try{
var c=localStorage.getItem(${JSON.stringify(THEME_STORAGE_KEY)});
if(c!=='system'&&c!=='light'&&c!=='dark')c=${JSON.stringify(DEFAULT_THEME_CHOICE)};
var t=c==='system'?(window.matchMedia('(prefers-color-scheme: light)').matches?'light':'dark'):c;
var r=document.documentElement;
r.dataset.theme=t;
r.classList.toggle('dark',t==='dark');
r.style.colorScheme=t;
${ORPHANED_CART_KEYS.map((k) => `localStorage.removeItem(${JSON.stringify(k)});`).join('')}
}catch(e){}})()`

/** Read the stored choice. Safe to call on the server (returns the default). */
export function readThemeChoice(): ThemeChoice {
  if (typeof window === 'undefined') return DEFAULT_THEME_CHOICE
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY)
    return isThemeChoice(raw) ? raw : DEFAULT_THEME_CHOICE
  } catch {
    return DEFAULT_THEME_CHOICE
  }
}

/** What the OS is asking for right now. */
export function systemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return DEFAULT_THEME_CHOICE === 'light' ? 'light' : 'dark'
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
}

export function resolveTheme(choice: ThemeChoice): ResolvedTheme {
  return choice === 'system' ? systemTheme() : choice
}

/**
 * Stamp the document. The single writer — the boot script above does the same
 * three things, and they must stay the same three things.
 *
 * `class="dark"` rides along beside `data-theme` because shadcn's `dark:`
 * variant is defined as `&:where(.dark, .dark *)`; dropping it would leave
 * every `dark:` utility in the app stuck on.
 */
export function applyTheme(choice: ThemeChoice): ResolvedTheme {
  const resolved = resolveTheme(choice)
  const root = document.documentElement
  root.dataset['theme'] = resolved
  root.classList.toggle('dark', resolved === 'dark')
  root.style.colorScheme = resolved
  return resolved
}

/** Persist a choice. Storage failures are not worth surfacing to a visitor. */
export function storeThemeChoice(choice: ThemeChoice): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, choice)
  } catch {
    // Ignored on purpose — the theme still applies for this session.
  }
}
